import { Coords } from '../../models';

export class JourneyPoint {
    private static pointIdCounter: number = 0;
    public id: number = 0;

    public isStartPoint: boolean = true;
    public isEndPoint: boolean = false;

    constructor(
        public name: string, // Point name (normally inherited from route ie. Bus 150S)
        public label: string, // Short name or label for map (ie. W for walk and line for 150S)
        public place: string, // Name of point place (ie. Islev Station)
        public coords: Coords,
        public type: JourneyPointType = JourneyPointType.WALK_POINT,
        public journeyType: JourneyType = JourneyType.WALK) {
        // Constructor
        this.id = JourneyPoint.genId();
    }

    /**
     * Generate a unique id for this point instance
     */
    private static genId() {
        return this.pointIdCounter++ % Number.MAX_VALUE;
    }

    /**
    * Convert to geo json compatible features
    */
    public asGeoJson(): JourneyPointFeature {
        return {
            type: "Feature",
            properties: {
                id: this.id,
                pointType: this.type,
                journeyType: this.journeyType,
                name: this.name,
                label: this.label,
                place: this.place,
                isStartPoint: this.isStartPoint,
                isEndPoint: this.isEndPoint
            },
            geometry: {
                type: 'Point',
                coordinates: this.coords.longLat()
            }
        }
    }
}

export class Journey {
    // Dutation in minutes
    duration: number = 0;
    distance: number = 0;
    routes: JourneyRoute[];
}

export class JourneyRoute {
    name: string = "Journey Route";
    type: JourneyType = JourneyType.UNKNOWN;
    start: Coords = null;
    end: Coords = null;
    points: JourneyPoint[] = [];

    public startPoint(): JourneyPoint {
        return this.points.length > 0 ? this.points[0] : null;
    }

    public endPoint(): JourneyPoint {
        return this.points.length > 0 ? this.points[this.points.length - 1] : null;
    }

    /**
    * Convert route points to geo json line string
    */
    public asGeoJson(): JourneyLineString {
        return {
            type: 'LineString',
            coordinates: this.points.map(point => point.coords.longLat()) || []
        }
    }
}

/**
* LineString in / compatible with GeoJson LineString
*/
export interface JourneyLineString {
    type: "LineString";
    coordinates: [number, number][];
}

/**
* Journey point feature compatible with GeoJson Feature (with Journey specific properties)
*/
export interface JourneyPointFeature {
    "type": "Feature",
    "properties": {
        'id': number,
        'journeyType': JourneyType,
        'pointType': JourneyPointType,
        'name': string,
        'label': string,
        'place': string,
        'isStartPoint': boolean,
        'isEndPoint': boolean
    },
    "geometry": {
        type: 'Point',
        coordinates: [number, number]
    }
}

export enum JourneyType {
    WALK = 'WALK',
    BUS = 'BUS',
    NIGHT_BUS = 'NIGHT_BUS',
    EXTRA_BUS = 'EXTRA_BUS',
    TELE_BUS = 'TELE_BUS',
    METRO = 'METRO',
    REG_TRAIN = 'REG_TRAIN',
    TRAIN = 'TRAIN',
    S_TRAIN = 'S_TRAIN',
    FAST_TRAIN = 'FAST_TRAIN',
    IC_TRAIN = 'IC_TRAIN',
    FERRY = 'FERRY',
    OTHER = 'OTHER',
    UNKNOWN = 'UNKNOWN'
}

export enum JourneyPointType {
    WALK_POINT = 'WALK_POINT',
    TRAIN_STATION = 'TRAIN_STATION',
    METRO_STATION = 'METRO_STATION',
    BUS_STOP = 'BUS_STOP',
    FERRY_STOP = 'FERRY_STOP',
    SEARCH_RESULT = 'SEARCH_RESULT',
    OTHER_POINT = 'OTHER_POINT'
}
